/**
 * AbilityGroundsArena — the consolidated Ability Testing Grounds map.
 *
 * The single framed home for testing classes + abilities (folds in the old
 * /abilitytest range map + AbilityTestArena dummies). Design goals:
 * - Deterministic far-left player spawn (no RNG) so captures are repeatable.
 * - Dark, high-contrast background so VFX read cleanly for quality images.
 * - A row of standing dummy pigs at KNOWN x-positions (range testing) — the
 *   data-driven AbilityGroundsPanel (Phase 2) spawns additional configurable
 *   bots (dummy / standing-caster / aggressive) for scenarios on top of these.
 * - A raised one-way platform for vertical / ground-targeted ability testing.
 *
 * Map id 'ability-grounds-arena'. Loaded via the /abilitygrounds coercion
 * (?map=ability-grounds-arena). Modeled on AbilityTestArena's structure so it
 * loads through the same LevelFormatConverter path.
 */

import { Platform } from '../../shared/CharacterTypes';
import { MAP_DEFAULTS } from '../../config/map/defaults.config';
import { GridLevel, LayerType } from '../../shared/TileEditorTypes';
import { W, H, T } from './StarterMap';
import {
  ABILITY_GROUNDS_FLOOR_Y,
  ABILITY_GROUNDS_MAP_ID,
  ABILITY_GROUNDS_SPAWN,
  ABILITY_GROUNDS_TARGETS,
} from '../../shared/qa/AbilityGroundsLab';

const MAP_NAME = 'Ability Grounds';
const VERSION = '1.0';

// Empty editor layers
const groundLayer = new Map<string, number>();
const collisionLayer = new Map<string, number>();
const objectsLayer = new Map<string, number>();
const entitiesLayer = new Map<string, number>();
const triggersLayer = new Map<string, number>();

export const GROUNDS_FLOOR_Y = ABILITY_GROUNDS_FLOOR_Y;
export const GROUNDS_SPAWN_X = ABILITY_GROUNDS_SPAWN.x;
// Player is exactly 40×40, so floor - 40 = feet on the ground. Do NOT
// generalize this -40 to other entity types (see LEVEL_FORMATS_EXPLAINED.md).
export const GROUNDS_SPAWN_Y = ABILITY_GROUNDS_SPAWN.y;

// Pig hitbox height — entity.y is the TOP of the hitbox (60×50 pig).
const PIG_HEIGHT = 50;

// Effectively-immortal training dummies (WoW-style): a huge HP pool so sustained
// ability iteration + the class×ability matrix runner never run out of a target.
const DUMMY_HP = 999999;

// Standing dummies at known distances from spawn (px from spawn shown for
// quick range reference). Spawned bots (Phase 2) sit between/around them.
export const GROUNDS_DUMMY_POSITIONS = ABILITY_GROUNDS_TARGETS.map((target) => ({
  x: target.x,
  y: GROUNDS_FLOOR_Y - PIG_HEIGHT,
}));

const entities: any[] = GROUNDS_DUMMY_POSITIONS.map((pos, i) => ({
  type: 'pig',
  id: ABILITY_GROUNDS_TARGETS[i].id,
  x: pos.x,
  y: pos.y,
  properties: { displayName: `Dummy ${i + 1}`, hp: DUMMY_HP },
}));

const spawnPoints = [
  { x: GROUNDS_SPAWN_X / T, y: GROUNDS_SPAWN_Y / T },
];

const defaultSpawn = {
  x: GROUNDS_SPAWN_X,
  y: GROUNDS_SPAWN_Y,
};

const runtimePlatforms: Platform[] = [
  // Main ground — full width.
  {
    id: 'grounds-floor',
    x: 0,
    y: GROUNDS_FLOOR_Y,
    width: W * T,
    height: 40,
    type: 'normal' as const,
    color: '#262b3a',
  },
  // Raised one-way platform for vertical / ground-targeted ability testing.
  {
    id: 'grounds-platform-upper',
    x: GROUNDS_SPAWN_X + 500,
    y: GROUNDS_FLOOR_Y - 180,
    width: 360,
    height: 20,
    type: 'normal' as const,
    color: '#3a4256',
    isOneWay: true,
  },
];

export const AbilityGroundsArena: GridLevel & Partial<any> = {
  id: ABILITY_GROUNDS_MAP_ID,
  name: MAP_NAME,
  version: VERSION,
  widthTiles: W,
  heightTiles: H,
  width: W * T,
  height: H * T,
  tileSize: T,
  backgroundColor: '#14161f',
  gravity: MAP_DEFAULTS.gravity,

  spawnPoints,
  defaultSpawn,

  layers: [
    { name: LayerType.GROUND, visible: true, locked: false, opacity: 1.0, tiles: groundLayer },
    { name: LayerType.COLLISION, visible: true, locked: false, opacity: 1.0, tiles: collisionLayer },
    { name: LayerType.OBJECTS, visible: true, locked: false, opacity: 1.0, tiles: objectsLayer },
    { name: LayerType.ENTITIES, visible: true, locked: false, opacity: 1.0, tiles: entitiesLayer, entities },
    { name: LayerType.TRIGGERS, visible: true, locked: false, opacity: 1.0, tiles: triggersLayer },
  ],

  platforms: runtimePlatforms,
  spawnRules: [],
  type: 'field',
  metadata: {
    author: 'Helix2000 System',
    description: 'Consolidated ability testing grounds — framed arena with standing dummies, data-driven ability picker, scenario bots, render-truth readout, and quality capture.',
    difficulty: 'Low',
    maxPlayers: 8,
    version: VERSION,
    tags: ['testing', 'arena', 'abilities', 'grounds', 'dummies'],
    // lobby_safe baseline, PvP OFF (K9 ruling, 2026-07-16): with PvP on +
    // free-for-all friendly fire, one bot's AoE (seed_of_corruption measured
    // 28/25/24) splashed the other capture bots and polluted evidence. The
    // grounds is a measurement lab — dummies take the hits; player-vs-player
    // testing belongs on /brawl or /duel. botBehavior stays 'ignore'.
    profile: 'lobby_safe',
    overrides: {
      pvp: { enabled: false, botBehavior: 'ignore' },
    },
  },
};
